import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import type { LinkComponentProps } from "../types";

/**
 * Check if a URL is internal (relative path or same origin).
 */
function isInternalLink(url: string): boolean {
  if (!url || url === "#") return false;
  // Relative paths are internal
  if (url.startsWith("/") && !url.startsWith("//")) return true;
  // Absolute URLs to same origin would also be internal, but we treat them as external for simplicity
  return false;
}

/**
 * LinkComponent - renders an anchor link.
 * Uses React Router Link for internal links, regular anchor for external.
 * Supports static href/text from props or dynamic interpolation from data.
 */
function LinkComponent({ href, text, data }: LinkComponentProps) {
  const { t } = useTranslation();

  const displayText =
    text != null && String(text).trim() !== ""
      ? t(text)
      : data != null && "text" in data
        ? String(data.text ?? "")
        : "";

  const displayHref =
    href != null && String(href).trim() !== ""
      ? href
      : data != null && "href" in data
        ? String(data.href ?? "")
        : "#";

  const linkContent = displayText || displayHref;

  if (isInternalLink(displayHref)) {
    return (
      <RouterLink to={displayHref} className="text-primary hover:underline">
        {linkContent}
      </RouterLink>
    );
  }

  return (
    <a
      href={displayHref}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline"
    >
      {linkContent}
    </a>
  );
}

export { LinkComponent };
